import GenericHead from 'components/GenericHead';
import RichResults from 'components/RichResults';
import useTranslate from 'contexts/TranslateContext/useTranslate';
import { Genre } from 'state/Genres/types';
import { get } from 'lodash-es';
import { getScaledImageUrlById } from 'utils/url';
import { Helmet } from 'react-helmet';
import { HighlightsMetadataService } from 'services/HighlightsMetadataService';
import { highlightsPlayerLink } from 'utils/HighlightsUtils';
import { Market } from 'state/Location/types';
import { link as playerLink, meta as playerMeta } from 'heads/player';
import { STATION_TYPE } from 'constants/stationTypes';
import { translateKeywords } from 'utils/metaUtils';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ViewName } from 'views/PageBody';
import {
  WIDGET_DIMENSIONS,
  WidgetDimensions,
} from 'constants/widgetDimensions';
import type { HighlightsMetadata } from 'api/highlights';

export type Props = {
  band: string;
  callLetters: string;
  city: string;
  description: string;
  freq: string;
  genre: Genre | Record<string, any>;
  id: string;
  isSubSection?: boolean;
  logo: string;
  market: Market;
  metaTitle?: string;
  pagePath: string;
  seedId: string | number;
  seedType: string;
  siteUrl: string;
  stateAbbreviation: string;
  stationName: string;
  website: string;
};

export default function Head({
  band,
  callLetters,
  city,
  description,
  freq,
  genre,
  id,
  isSubSection = false,
  logo,
  market,
  metaTitle,
  pagePath,
  seedId,
  seedType,
  siteUrl,
  stateAbbreviation,
  stationName,
  website,
}: Props) {
  const translate = useTranslate();
  const params = useParams<{ highlightsSlug?: string }>();
  const [highlightsMetadata, setHighlightsMetadata] =
    useState<HighlightsMetadata | null>(null);

  // Extract highlights slug from route params
  const { highlightsSlug } = params;

  // Fetch clip metadata when highlightsSlug is provided
  useEffect(() => {
    if (
      highlightsSlug &&
      HighlightsMetadataService.hasValidClipId(highlightsSlug)
    ) {
      HighlightsMetadataService.getHighlightsMetadata(highlightsSlug)
        .then(metadata => {
          if (metadata) {
            setHighlightsMetadata(metadata);
          }
        })
        .catch(() => {
          // Silently fail - use default station metadata
        });
    }
  }, [highlightsSlug]);

  const title = metaTitle || stationName;

  // Use clip metadata if available, otherwise use station metadata
  const pageTitle = highlightsMetadata?.title || title;
  const pageDescription = highlightsMetadata?.description || description;
  const pageImage =
    highlightsMetadata?.preview_image ||
    highlightsMetadata?.image ||
    (logo && logo.startsWith('//') ? `https:${logo}` : logo) ||
    getScaledImageUrlById(seedId as unknown as number, seedType);

  const keywords = translateKeywords(
    translate,
    'iHeartRadio, iHeart, Listen, Live, Music, Talk, Radio, Online, Streaming, Free',
  );
  const dynamicKeywords = translate(
    '{stationName}, {stateAbbreviation}, {city}, {genre}',
    {
      city,
      genre: get(genre, 'name'),
      stateAbbreviation,
      stationName,
    },
  );

  const { height, fixedWidth: width }: WidgetDimensions =
    WIDGET_DIMENSIONS.LIVE ?? {};

  return (
    <>
      <GenericHead
        deeplink={`goto/live/${seedId}`}
        description={stationName ? pageDescription : undefined}
        facebookImage={pageImage}
        image={pageImage}
        legacyDeeplink={`play/live/${seedId}`}
        metaDescription={pageDescription}
        ogType={isSubSection ? 'website' : 'music.radio_station'}
        title={pageTitle}
        twitterCard="product"
        twitterImage={pageImage}
      />
      <Helmet
        link={[
          ...playerLink({
            pagePath: pagePath ?? '',
            siteUrl,
            title: pageTitle,
          }),
          // Preconnect to Highlights SDK domains for faster loading
          ...highlightsPlayerLink(),
        ]}
        meta={[
          ...(isSubSection ?
            []
          : playerMeta({ height, pagePath: pagePath ?? '', siteUrl, width })),
          { content: `${keywords}, ${dynamicKeywords}`, name: 'keywords' },
          {
            content: translate('Genre'),
            name: 'twitter:label1',
          },
          { content: get(genre, 'name'), name: 'twitter:data1' },
          {
            content: translate('Location'),
            name: 'twitter:label2',
          },
          { content: get(market, 'name'), name: 'twitter:data2' },
        ]}
      />
      <RichResults
        meta={{
          broadcastDisplayName: stationName,
          broadcastFrequency: { band, frequency: freq },
          callLetters,
          description: pageDescription,
          id,
          keywords,
          market,
          seedType: STATION_TYPE.LIVE,
          stationName,
          url: pagePath,
          website,
        }}
        type={ViewName.LiveProfile}
      />
    </>
  );
}
